import React, { useState, useEffect } from 'react';
import { get, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ROLE_LABELS = {
  admin:'Admin', sales_manager:'Sales Manager', content_sales:'Content & Sales',
  inventory_officer:'Inventory Officer', production_supervisor:'Production Supervisor',
  production_assistant:'Production Assistant', production_staff:'Production Staff',
  tailor:'Tailor', driver:'Driver'
};

function lagosNow() {
  return new Date(Date.now() + 60*60*1000);
}

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [deptSubmissions, setDeptSubmissions] = useState({ data:[], missing:[] });
  const [shiftRequests, setShiftRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const today = lagosNow().toISOString().split('T')[0];

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/kiosk-sessions/today'),
      get('/departments/submissions/date/'+today),
      get('/kiosk-sessions/shift-requests')
    ]).then(([live, depts, shifts]) => {
      setData(live);
      setDeptSubmissions(depts);
      setShiftRequests((shifts.data||[]).filter(s=>s.status==='pending'));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const approveShift = async (id) => {
    try {
      await put('/kiosk-sessions/shift-requests/'+id+'/approve', {});
      load();
    } catch(err) { alert(err.message); }
  };

  const rejectShift = async (id) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    try {
      await put('/kiosk-sessions/shift-requests/'+id+'/reject', { rejection_reason: reason });
      load();
    } catch(err) { alert(err.message); }
  };

  if (!['super_admin','admin'].includes(user?.role)) {
    return <div className="empty-state"><h3>Access Restricted</h3></div>;
  }

  const sessions = data?.sessions || [];
  const notSignedIn = data?.not_signed_in || [];
  const onBreak = sessions.filter(s => s.on_break);
  const overBreak = sessions.filter(s => s.on_break && s.break_minutes_so_far > 30);
  const noReport = sessions.filter(s => !s.report_submitted && !s.sign_out_time);
  const signedOut = sessions.filter(s => s.sign_out_time);

  const StatTile = ({label, value, color, bg}) => (
    <div style={{background:bg||'#fff',border:'1px solid '+(color||'#556B2F')+'33',
      borderRadius:10,padding:'14px 16px'}}>
      <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',letterSpacing:'.1em',
        color:color||'#556B2F',marginBottom:6}}>{label}</div>
      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2rem',fontWeight:700,
        color:color||'#556B2F'}}>{value}</div>
    </div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Manager Dashboard</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Live attendance — {lagosNow().toLocaleDateString('en-NG',{weekday:'long',day:'numeric',month:'long'})}
            <span style={{marginLeft:8,fontSize:11,color:'#8A8A8A'}}>(Auto-refreshes every minute)</span>
          </p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      {/* Stat tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:20}}>
        <StatTile label="Present" value={sessions.filter(s=>!s.sign_out_time).length} color="#2E6B3A" bg="#E8F5EB" />
        <StatTile label="Not Signed In" value={notSignedIn.length} color="#8F2E24" bg="#FDEAEA" />
        <StatTile label="On Break" value={onBreak.length} color="#8B5E0A" bg="#FFF3DC" />
        <StatTile label="Over Break" value={overBreak.length} color="#8F2E24" bg="#FDEAEA" />
        <StatTile label="No Report" value={noReport.length} color="#8B5E0A" bg="#FFF3DC" />
        <StatTile label="Signed Out" value={signedOut.length} color="#556B2F" bg="#F2EFE7" />
        {shiftRequests.length > 0 && (
          <StatTile label="Shift Requests" value={shiftRequests.length} color="#8F2E24" bg="#FDEAEA" />
        )}
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,borderBottom:'1px solid var(--border-light)',paddingBottom:12}}>
        {[['live','Live View'],['checklist','Dept Checklists'],['shifts','Shift Requests']].map(([key,label])=>(
          <button key={key} className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
            onClick={()=>setActiveTab(key)}>
            {label}
            {key==='shifts' && shiftRequests.length > 0 && (
              <span style={{marginLeft:6,background:'#8F2E24',color:'#fff',
                borderRadius:10,padding:'0 6px',fontSize:10}}>{shiftRequests.length}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? <div className="spinner"></div> : (
        <>
          {activeTab==='live' && (
            <div>
              {/* Not signed in */}
              {notSignedIn.length > 0 && (
                <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
                  padding:'12px 16px',marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#8F2E24',marginBottom:8}}>
                    ⚠ Not yet signed in ({notSignedIn.length})
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    {notSignedIn.map(s => (
                      <span key={s.id} style={{fontSize:12,background:'#fff',
                        padding:'3px 10px',borderRadius:20,color:'#8F2E24',
                        border:'1px solid #F5B8B8'}}>{s.name}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Over break warning */}
              {overBreak.length > 0 && (
                <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,
                  padding:'12px 16px',marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#8B5E0A',marginBottom:8}}>
                    ⏰ Over break limit ({overBreak.length})
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    {overBreak.map(s => (
                      <span key={s.id} style={{fontSize:12,background:'#fff',
                        padding:'3px 10px',borderRadius:20,color:'#8B5E0A',
                        border:'1px solid #F5C842'}}>
                        {s.name} ({Math.round(s.break_minutes_so_far)}min)
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Staff table */}
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#556B2F'}}>
                  {['Staff','Role','Signed In','Break','Break Duration','Report','Status'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,
                      color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {sessions.map((s,i) => (
                    <tr key={s.id} style={{background:s.on_break?'#FFF8DC':i%2===0?'#fff':'#F9F9F9'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>{s.name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555',fontSize:11}}>{ROLE_LABELS[s.role]||s.role}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {s.sign_in_time ? new Date(s.sign_in_time).toISOString().substr(11,5) : '—'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {s.on_break ? (
                          <span style={{color:'#8B5E0A',fontWeight:600}}>On Break</span>
                        ) : s.break_duration > 0 ? (
                          <span style={{color:'#556B2F'}}>Done</span>
                        ) : '—'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',
                        color: s.break_duration > 30 ? '#8F2E24' : '#3B2A21'}}>
                        {s.break_duration > 0 ? s.break_duration+'min' :
                         s.on_break ? Math.round(s.break_minutes_so_far)+'min (ongoing)' : '—'}
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                          background:s.report_submitted?'#E8F5EB':'#FFF3DC',
                          color:s.report_submitted?'#2E6B3A':'#8B5E0A'}}>
                          {s.report_submitted?'✓ Submitted':'Pending'}
                        </span>
                      </td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                          background:s.sign_out_time?'#E8F5EB':'#EDF4FF',
                          color:s.sign_out_time?'#2E6B3A':'#1E4FAD'}}>
                          {s.sign_out_time?'Signed Out':'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr><td colSpan={7} style={{padding:20,textAlign:'center',color:'#8A8A8A'}}>
                      No staff have signed in today yet
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab==='checklist' && (
            <div>
              {deptSubmissions.missing?.length > 0 && (
                <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
                  padding:'12px 16px',marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#8F2E24',marginBottom:8}}>
                    ⚠ Departments not yet submitted ({deptSubmissions.missing.length})
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    {deptSubmissions.missing.map(d => (
                      <span key={d.id} style={{fontSize:12,background:'#fff',
                        padding:'3px 10px',borderRadius:20,color:'#8F2E24',
                        border:'1px solid #F5B8B8'}}>{d.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {deptSubmissions.data?.length === 0 && deptSubmissions.missing?.length === 0 ? (
                <div className="empty-state"><h3>No departments configured</h3></div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
                  {deptSubmissions.data?.map(s => (
                    <div key={s.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                      borderRadius:10,padding:16}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                        <div style={{fontSize:14,fontWeight:600,color:'#3B2A21'}}>{s.department_name}</div>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                          background:s.is_complete?'#E8F5EB':'#FFF3DC',
                          color:s.is_complete?'#2E6B3A':'#8B5E0A'}}>
                          {s.is_complete?'✓ Complete':'In Progress'}
                        </span>
                      </div>
                      <div style={{fontSize:11,color:'#8A8A8A',marginBottom:6}}>
                        Submitted by: {s.submitted_by_name}
                      </div>
                      {s.notes && (
                        <div style={{fontSize:11,color:'#555',background:'#F2EFE7',
                          padding:'6px 10px',borderRadius:6,marginTop:6}}>
                          📝 {s.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab==='shifts' && (
            <div>
              {shiftRequests.length === 0 ? (
                <div className="empty-state"><h3>No pending shift requests</h3></div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {shiftRequests.map(r => (
                    <div key={r.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                      borderRadius:10,padding:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:600,color:'#3B2A21'}}>
                          {r.staff_name} — {r.shift_type} shift
                        </div>
                        <div style={{fontSize:12,color:'#8A8A8A',marginTop:2}}>
                          Date: {r.date} · Requested by: {r.requested_by_name}
                        </div>
                        {r.reason && <div style={{fontSize:11,color:'#555',marginTop:4}}>{r.reason}</div>}
                      </div>
                      <div style={{display:'flex',gap:8}}>
                        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}}
                          onClick={()=>approveShift(r.id)}>✓ Approve</button>
                        <button className="btn btn-danger btn-sm"
                          onClick={()=>rejectShift(r.id)}>✗ Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
